import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { PortalLayout, RequireAuth } from "./components/layout"
import { AuthProvider, useAuth } from "./stores/auth"
import { DashboardPage } from "./pages/dashboard"
import { ApplyPage } from "./pages/apply"
import { LoanDetailPage } from "./pages/loan_detail"
import { LoginPage } from "./pages/login"
import { NotificationsPage } from "./pages/notifications"
import { OnboardingPage } from "./pages/onboarding"
import { PaymentsPage } from "./pages/payments"
import { ProfilePage } from "./pages/profile"
import { QuickLoanPage } from "./pages/quick_loan"
import { RegisterPage } from "./pages/register"
import { SupportPage } from "./pages/support"
import "./i18n"
import "./styles/app.css"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

function HomeRedirect() {
  const { client, isLoading } = useAuth()

  if (isLoading) {
    return <div className="screen-shell">Loading...</div>
  }

  if (!client) {
    return <Navigate replace to="/login" />
  }

  return <Navigate replace to="/" />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <PortalLayout />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="apply" element={<ApplyPage />} />
              <Route path="quick-loan" element={<QuickLoanPage />} />
              <Route path="loans/:loanId" element={<LoanDetailPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="support" element={<SupportPage />} />
            </Route>
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

if ("serviceWorker" in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener("load", async () => {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js")

        if (registration.waiting) {
          window.dispatchEvent(new CustomEvent("sw-update-available"))
        }

        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing
          if (!newWorker) return

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent("sw-update-available"))
            }
          })
        })

        let refreshing = false
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (!refreshing) {
            refreshing = true
            window.location.reload()
          }
        })
      } catch {
        // SW registration failed
      }
    })
  } else {
    void navigator.serviceWorker.getRegistrations().then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister())),
    )

    if ("caches" in window) {
      void caches.keys().then((keys) =>
        Promise.all(
          keys
            .filter(
              (key) =>
                key.startsWith("portal-app-") ||
                key.startsWith("portal-data-") ||
                key.startsWith("workbox-"),
            )
            .map((key) => caches.delete(key)),
        ),
      )
    }
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
